import { RefObject, useState } from "react";
import styled from "styled-components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { IconButton, Popover, Stack, Text } from "@fidelix/fx-miranda";
import { useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";
import { EditFormModal } from "../common/edit-form-modal";
import { useUpdateScadaGraphicLinkMutation } from "./graphics.mutation";
import { HasPermission } from "../utils/permission-helper";
import { Permissions } from "../const";

const LINK_FIELD_NAME = "updatedLink";

export type UpdateGraphicLinkParams = {
  companyId: number;
  graphicsId: number;
  graphic?: string;
  link: string;
};

type ScadaLinkPopoverProps = {
  linkId: string;
  editParams: UpdateGraphicLinkParams;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

export function EditScadaLinkPopover({
  linkId,
  editParams,
  triggerRef,
  onClose,
}: ScadaLinkPopoverProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { companyId, graphic, graphicsId, link } = editParams;
  const { mutateAsync, isPending: isMutating } =
    useUpdateScadaGraphicLinkMutation();

  const closeModal = () => {
    setEditModalOpen(false);
    onClose();
  };

  const onSubmit = async (values: Record<string, string>) => {
    await mutateAsync(
      {
        companyId,
        graphicsId,
        graphic,
        id: link,
        link: values[LINK_FIELD_NAME],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["graphics-attachments"],
          });
          closeModal();
        },
      },
    );
  };

  return (
    <>
      {editModalOpen && (
        <EditFormModal
          onClose={closeModal}
          title={<Trans>Edit link</Trans>}
          fields={[
            {
              name: LINK_FIELD_NAME,
              label: <Trans>Link</Trans>,
              defaultValue: link,
            },
          ]}
          onSubmit={onSubmit}
          isPending={isMutating}
          data-test-id="edit-link-modal"
        />
      )}
      <LinkTooltipPopover
        offset={2}
        triggerRef={triggerRef}
        isOpen
        isNonModal
        onOpenChange={onClose}
        placement="bottom"
        content={
          <>
            <Heading slot="title">
              <Stack axis="x" spacing="medium" align="center">
                <Text variant="subtitle2Bold">{linkId}</Text>
                <Stack axis="x" spacing="xxsmall" align="center">
                  <HasPermission permission={Permissions.SCADAGRAPHICS_EDIT}>
                    <IconButton
                      icon="edit"
                      size="small"
                      variant="transparent"
                      aria-label={t`Edit link`}
                      onPress={() => setEditModalOpen(true)}
                    />
                  </HasPermission>
                </Stack>
                <IconButton
                  icon="close"
                  size="small"
                  variant="transparent"
                  aria-label="Close"
                  onPress={onClose}
                  style={{ marginLeft: "auto" }}
                />
              </Stack>
            </Heading>
          </>
        }
      />
    </>
  );
}

const LinkTooltipPopover = styled(Popover)`
  border-radius: ${(p) => p.theme.space("xsmall")};
  border-color: ${(p) => p.theme.colors.lightNeutral};
  box-shadow: 4px 4px 4px ${(p) => p.theme.colors.black}26;
`;
